import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, RefreshCw, Wallet, X, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const fmt = (value) => new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(value || 0));
const money = (value) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(Number(value || 0));
const date = (value) => value ? new Date(value).toLocaleString("es-CO") : "—";
const statusLabel = { pending: "Pendiente", approved: "Aprobado y pagado", rejected: "Rechazado", available: "Disponible" };

export const MyFinance = () => {
  const [data, setData] = useState({ wallet: {}, ledger: [], withdrawals: [], exchange: {} });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const withdrawalKeyRef = useRef("");

  const load = useCallback(async () => {
    try { setData((await api.get("/api/finance/me")).data); }
    catch (error) { setNotice(formatApiError(error)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const wallet = data.wallet || {};
  const rate = Number(wallet.cop_per_energy || data.exchange?.cop_per_energy || 100);
  const requestedEnergy = amount ? Number(amount) : Number(wallet.available || 0);
  const previewCop = Math.round(Math.max(0, requestedEnergy || 0) * rate);

  const withdraw = async () => {
    if (busy) return;
    if (!withdrawalKeyRef.current) {
      withdrawalKeyRef.current = globalThis.crypto?.randomUUID?.() || `withdraw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    setBusy(true); setNotice("");
    try {
      const { data: result } = await api.post("/api/finance/withdrawals", {
        amount: amount ? Number(amount) : null,
        idempotency_key: withdrawalKeyRef.current,
      });
      withdrawalKeyRef.current = "";
      setAmount("");
      setNotice(`Retiro solicitado: ${fmt(result.energy_amount)} KD = ${money(result.cop_amount)} a una tasa congelada de 1 KD = ${money(result.cop_per_energy)}.`);
      await load();
    } catch (error) {
      setNotice(formatApiError(error));
    } finally { setBusy(false); }
  };

  const cards = [
    ["Saldo disponible", wallet.available, wallet.available_cop],
    ["Saldo pendiente", wallet.pending, wallet.pending_cop],
    ["Total retirado", wallet.withdrawn, wallet.withdrawn_cop],
  ];

  return <PageShell kicker="Economía KD9" title="MIS FINANZAS"
    desc="Solo las ganancias desbloqueadas son retirables. El sistema convierte Energía KD9 a COP con la equivalencia vigente y congela esa tasa al solicitar el retiro."
    badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2"><RefreshCw size={15} /></button>}>
    {notice && <div className="kd9-card mb-5 p-4 font-mont text-sm">{notice}</div>}

    <section className="kd9-card mb-5 p-5 border border-amber-400/20">
      <div className="flex items-center gap-3"><Zap size={18}/><div><div className="font-cinzel kd9-gold-text">EQUIVALENCIA DE RETIRO</div><div className="mt-1 text-lg">1 Energía KD9 = <strong>{money(rate)} COP</strong></div><p className="mt-1 text-xs opacity-60">Solo aplica a ganancias retirables. No define precios de paquetes, recargas, servicios ni promociones. Cada retiro conserva su tasa histórica.</p></div></div>
    </section>

    <div className="grid gap-4 md:grid-cols-3">
      {cards.map(([label, energy, copValue]) => <div key={label} className="kd9-card p-5">
        <div className="font-mont text-[10px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
        <div className="mt-2 font-cinzel text-2xl kd9-gold-text">{fmt(energy)} KD</div>
        <div className="mt-1 text-sm opacity-60">≈ {money(copValue)}</div>
      </div>)}
    </div>

    <section className="kd9-card mt-5 p-5">
      <h2 className="font-cinzel text-xl">Solicitar retiro</h2>
      <p className="mt-1 font-cormorant text-lg" style={{ color: "var(--kd9-text-soft)" }}>Escribe Energías KD9. Déjalo vacío para retirar todo el saldo disponible.</p>
      <div className="mt-4 grid gap-3 md:grid-cols-[220px_1fr_auto] md:items-center">
        <input className="kd9-input px-3 py-3" type="number" min="0.01" step="0.01" max={wallet.available || undefined} placeholder="Energías KD" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <div className="rounded-lg border border-white/10 p-3 text-sm">
          <span className="opacity-60">Conversión automática:</span> <strong>{fmt(requestedEnergy)} KD × {money(rate)} = {money(previewCop)}</strong>
        </div>
        <button className="kd9-btn-primary rounded-lg px-5 py-3 font-mont text-[10px]" disabled={busy || Number(wallet.available || 0) <= 0 || requestedEnergy > Number(wallet.available || 0)} onClick={withdraw}>
          <Wallet size={14} className="mr-2 inline" />{busy ? "Procesando…" : "Solicitar retiro"}
        </button>
      </div>
    </section>

    <section className="kd9-card mt-5 overflow-hidden">
      <div className="border-b p-5" style={{ borderColor: "var(--kd9-border)" }}><h2 className="font-cinzel text-xl">Historial económico</h2></div>
      <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-left font-mont text-xs"><thead><tr style={{ color: "var(--kd9-text-soft)" }}>{["Fecha", "Movimiento", "Origen", "Estado", "Energía"].map(x=><th key={x} className="p-4">{x}</th>)}</tr></thead>
      <tbody>{data.ledger.map(row=><tr key={row.id} className="border-t" style={{ borderColor: "var(--kd9-border)" }}><td className="p-4">{date(row.created_at)}</td><td className="p-4">{row.description}</td><td className="p-4">{row.source_type || "—"}</td><td className="p-4">{statusLabel[row.status] || row.status}</td><td className="p-4 kd9-gold-text">{fmt(row.amount)} KD</td></tr>)}</tbody></table>
      {!data.ledger.length && <div className="p-8 text-center">Aún no hay movimientos económicos.</div>}</div>
    </section>

    {!!data.withdrawals?.length && <section className="kd9-card mt-5 p-5">
      <h2 className="font-cinzel text-xl">Solicitudes de retiro</h2>
      <div className="mt-4 grid gap-3">{data.withdrawals.map(row=><div key={row.id} className="rounded-xl border border-white/10 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3"><strong>{fmt(row.energy_amount)} KD → {money(row.cop_amount)}</strong><span>{statusLabel[row.status] || row.status}</span></div>
        <div className="mt-1 text-xs opacity-60">Tasa congelada: 1 KD = {money(row.cop_per_energy)} · Versión {row.exchange_version} · {date(row.requested_at)}</div>
      </div>)}</div>
    </section>}
  </PageShell>;
};

export const WithdrawalManagement = () => {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("pending");
  const [notice, setNotice] = useState("");
  const [exchange, setExchange] = useState({ cop_per_energy: 100, version: 1 });
  const [exchangeInput, setExchangeInput] = useState("100");
  const [exchangeReason, setExchangeReason] = useState("");
  const [exchangeBusy, setExchangeBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [withdrawals, rate] = await Promise.all([
        api.get("/api/director/finance/withdrawals", { params: state ? { state } : {} }),
        api.get("/api/director/finance/exchange-rate"),
      ]);
      setRows(withdrawals.data); setExchange(rate.data); setExchangeInput(String(rate.data.cop_per_energy || 100));
    } catch (error) { setNotice(formatApiError(error)); }
  }, [state]);
  useEffect(() => { load(); }, [load]);

  const saveExchangeRate = async (event) => {
    event.preventDefault();
    if (exchangeBusy) return;
    setExchangeBusy(true); setNotice("");
    try {
      const { data: result } = await api.put("/api/director/finance/exchange-rate", {
        cop_per_energy: Number(exchangeInput),
        reason: exchangeReason.trim(),
      });
      setExchange(result);
      setExchangeInput(String(result.cop_per_energy));
      setExchangeReason("");
      setNotice(`Equivalencia de retiro actualizada: 1 KD = ${money(result.cop_per_energy)} COP.`);
      await load();
    } catch (error) { setNotice(formatApiError(error)); }
    finally { setExchangeBusy(false); }
  };

  const decide = async (row, action) => {
    const promptText = action === "approve" ? "Referencia o soporte del pago realizado (obligatorio)" : "Motivo del rechazo (obligatorio)";
    const value = window.prompt(promptText);
    if (!value?.trim()) return;
    try {
      const body = action === "approve" ? { payment_reference: value.trim() } : { reason: value.trim() };
      await api.post(`/api/director/finance/withdrawals/${row.id}/${action}`, body);
      setNotice(action === "approve" ? "Retiro aprobado y auditado con la tasa histórica de su solicitud." : "Retiro rechazado; la Energía regresó al saldo disponible.");
      await load();
    } catch (error) { setNotice(formatApiError(error)); }
  };

  return <PageShell kicker="Dirección · Gobierno económico" title="GESTIÓN DE RETIROS"
    desc="La equivalencia KD/COP vive únicamente en este circuito de retiros. Dirección controla la tasa y cada solicitud conserva su versión histórica."
    badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2"><RefreshCw size={15} /></button>}>
    {notice && <div className="kd9-card mb-5 p-4 font-mont text-sm">{notice}</div>}
    <form onSubmit={saveExchangeRate} className="kd9-card mb-5 p-5">
      <div className="font-cinzel kd9-gold-text">EQUIVALENCIA EXCLUSIVA DE RETIROS</div>
      <p className="mt-1 text-sm opacity-60">Solo convierte ganancias retirables a COP. No altera Energía comprada, paquetes, servicios, bonos ni promociones.</p>
      <div className="mt-4 grid gap-3 md:grid-cols-[220px_1fr_auto] md:items-end">
        <label className="text-xs opacity-70">COP por 1 KD<input required min="1" max="1000000" type="number" className="kd9-input mt-1 w-full p-3" value={exchangeInput} onChange={(e)=>setExchangeInput(e.target.value)}/></label>
        <label className="text-xs opacity-70">Motivo del cambio<input required minLength="3" maxLength="500" className="kd9-input mt-1 w-full p-3" placeholder="Ej. Ajuste de tasa de retiro" value={exchangeReason} onChange={(e)=>setExchangeReason(e.target.value)}/></label>
        <button disabled={exchangeBusy || Number(exchangeInput)<=0} className="kd9-btn-primary rounded-lg px-5 py-3 font-mont text-[10px]">{exchangeBusy ? "Guardando…" : "Actualizar tasa de retiro"}</button>
      </div>
      <div className="mt-3 text-xs opacity-60">Vigente: 1 KD = {money(exchange.cop_per_energy)} COP · versión {exchange.version || 1}</div>
    </form>
    <div className="mb-5 flex flex-wrap gap-2">{[["pending","Pendientes"],["approved","Pagados"],["rejected","Rechazados"],["","Todos"]].map(([v,l])=><button key={l} onClick={()=>setState(v)} className={state===v ? "kd9-btn-primary rounded-lg px-4 py-2 font-mont text-[10px]" : "kd9-btn-outline rounded-lg px-4 py-2 font-mont text-[10px]"}>{l}</button>)}</div>
    <div className="grid gap-4">{rows.map(row=><article key={row.id} className="kd9-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4"><div><div className="font-cinzel text-xl">{row.user_name || row.user_email}</div><div className="mt-1 font-mont text-[10px]" style={{color:"var(--kd9-text-soft)"}}>{row.role} · {date(row.requested_at)}</div>
      <div className="mt-3 font-cinzel text-2xl kd9-gold-text">{fmt(row.energy_amount)} KD → {money(row.cop_amount)}</div><div className="mt-1 text-xs opacity-60">Tasa congelada: 1 KD = {money(row.cop_per_energy)} · versión {row.exchange_version}</div></div><span className="rounded-full border px-3 py-1 font-mont text-[9px]">{statusLabel[row.status] || row.status}</span></div>
      {row.reason && <p className="mt-3 font-cormorant text-lg">Motivo: {row.reason}</p>}{row.payment_reference && <p className="mt-3 font-mont text-xs">Pago: {row.payment_reference}</p>}
      {row.status==="pending" && <div className="mt-4 flex gap-2"><button onClick={()=>decide(row,"approve")} className="kd9-btn-primary rounded px-4 py-2 font-mont text-[10px]"><Check size={13} className="mr-1 inline"/>Aprobar y pagar</button><button onClick={()=>decide(row,"reject")} className="kd9-btn-outline rounded px-4 py-2 font-mont text-[10px]"><X size={13} className="mr-1 inline"/>Rechazar</button></div>}
    </article>)}</div>
    {!rows.length && <div className="kd9-card p-10 text-center">No hay retiros en este estado.</div>}
  </PageShell>;
};
